import React, { useState, useEffect } from "react";
import ReactFlow, { MiniMap, Controls, Background, applyNodeChanges, applyEdgeChanges ,} from "reactflow";
import "reactflow/dist/style.css";
import { Modal, Button, Card, Typography, Empty, Spin, message, Tooltip } from "antd";
import { 
  EyeOutlined, 
  DeleteOutlined, 
  SearchOutlined, 
  ProjectOutlined,
  FileTextOutlined,
  BranchesOutlined,
  FilterOutlined
} from '@ant-design/icons';
import axios from '../../services/axios';

const { Title, Text } = Typography;

const ViewWorkflow = () => {
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const user = localStorage.getItem('user');
  const projectName = JSON.parse(localStorage.getItem('Project'))        
  const isSuperUser = localStorage.getItem('isSuperUser') === 'true';


  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      let response;
      if(isSuperUser){
        response = await axios.post('get-workflow', { user })
      } else {
        response = await axios.post('user-workflow', { user, projectName: projectName });
      }

  
      if (!response.data.success) {
        console.warn("Error fetching workflows:", response.data.message || "Unknown error");
        setWorkflows([]); 
        return;
      }
  
      if (!Array.isArray(response.data.workflows)) {
        console.error("Invalid response: workflows is not an array", response.data.workflows);
        setWorkflows([]);
        return;
      }
  
      setWorkflows(response.data.workflows);
    } catch (error) {
      console.error("Error fetching workflows:", error.message);
      setWorkflows([]); 
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const handleViewWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setNodes(workflow.nodes || []);
    setEdges(workflow.edges || []);
    setIsModalVisible(true); 
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedWorkflow(null);
    setNodes([]);
    setEdges([]);
  };

  const handleDelete = (workflow) => {
    setWorkflowToDelete(workflow);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteWorkflow = async () => {
    if (!workflowToDelete) return;
    setDeleteLoading(true);
    try {
      await axios.post("delete-workflow", { code: workflowToDelete.code, user });
      message.success("Workflow deleted successfully");
      fetchWorkflows()
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
      setWorkflowToDelete(null);
    } catch (err) {
      setDeleteLoading(false)
      if (err.response && err.response.data && err.response.data.msg) {
        message.warning(err.response.data.msg);
      } else if (err.response && err.response.data && err.response.data.userName) {
        message.warning(err.response.data.userName[0]);
      } else if (err.response && err.response.status === 401) {
        message.error('User not authorized');
      } else {
        message.error(`Something went wrong`);
      }
    }
  };

   const formatArrayData = (data) => {
    if (Array.isArray(data)) {
      return data.join(", ");
    }
    return data || "Not specified";
  };

  // Handle node and edge changes
  const onNodesChange = (changes) => setNodes((nds) => applyNodeChanges(changes, nds));
  const onEdgesChange = (changes) => setEdges((eds) => applyEdgeChanges(changes, eds));

  return (
    <div className="p-10 overflow-y-auto min-h-screen">
      {/* <Title level={2} style={{ color: "#1a1a1a", marginBottom: "24px" }}>View Workflow</Title> */}
      {loading ? (
        <Spin size="large" />
      ) : workflows.length === 0 ? (
        <Empty description="No workflows available" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {workflows.map((workflow) => (
            <Card 
              key={workflow.code}
              className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl 
                        rounded-2xl shadow-lg hover:shadow-2xl transition-transform transform 
                        hover:scale-105 p-3 sm:p-4 bg-white mx-auto"
            >
              <div className="mb-3">
                {/* Project Name */}
                <div className="mb-2">
                  <h3 className="text-base sm:text-lg font-semibold text-blue-800 flex items-center gap-2 border-b pb-1">
                    <span className="text-purple-600"></span>
                    {Array.isArray(workflow.projectName)
                      ? workflow.projectName.join(", ")
                      : workflow.projectName || "NA"}
                  </h3>
                </div>

                {/* Workflow Name */}
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
                    <FileTextOutlined className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Text className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Workflow Name
                    </Text>
                    <p className="text-sm font-semibold text-gray-900 mt-1 break-words leading-5">
                      {formatArrayData(workflow.workflowName)}
                    </p>
                  </div>
                </div>

                {/* Workflow Description */}
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-green-50 rounded-lg flex-shrink-0">
                    <ProjectOutlined className="text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Text className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Description
                    </Text>
                    <p className="text-sm text-gray-700 mt-1 break-words leading-5 line-clamp-3">
                      {formatArrayData(workflow.workflowDescription) === "Not specified" 
                        ? "No description provided" 
                        : formatArrayData(workflow.workflowDescription)}
                    </p>
                  </div>
                </div>
              </div>  
                    
              <div className="px-5 pb-5">
                <div className="flex gap-2">
                  <Tooltip title="View workflow">
                    <Button 
                      type="primary" 
                      icon={<EyeOutlined />}
                      className="flex-1 h-10 rounded-xl font-medium bg-gradient-to-r from-blue-600 to-purple-600 border-0 hover:from-blue-700 hover:to-purple-700"
                      onClick={() => handleViewWorkflow(workflow)}
                    >
                      View Details
                    </Button>
                  </Tooltip>
                  {isSuperUser && (
                    <Tooltip title="Delete workflow">
                      <Button 
                        danger
                        icon={<DeleteOutlined />}
                        className="h-10 px-4 rounded-xl font-medium"
                        onClick={() => handleDelete(workflow)}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        title={
          <>
            Type: {Array.isArray(selectedWorkflow?.documentType) 
              ? selectedWorkflow.documentType.join(", ") 
              : selectedWorkflow?.documentType || "Workflow"}
            <br />
            Category: {Array.isArray(selectedWorkflow?.documentCategory) 
              ? selectedWorkflow.documentCategory.join(", ") 
              : selectedWorkflow?.documentCategory || "Not specified"}
          </>
        }        
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button key="close" onClick={handleCloseModal} style={{ backgroundColor: "red", color: "white" }}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedWorkflow ? (
          <div style={{ height: "500px", background: "#f4f4f4", padding: "10px", borderRadius: "8px" }}>
            <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} fitView>
              <MiniMap />
              <Controls />
              <Background variant="dots" gap={12} size={1} />
            </ReactFlow>
          </div>
        ) : (
          <Empty description="No workflow data available" />
        )}
      </Modal>
      <Modal
        title="Confirm Deletion"
        open={isDeleteModalVisible}
        onOk={handleDeleteWorkflow}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText={deleteLoading ? "Deleting..." : "Delete"}
        okButtonProps={{ danger: true, loading: deleteLoading, disabled: deleteLoading }}
        cancelButtonProps={{ disabled: deleteLoading }}
      >
        <p>Do you want to delete this workflow?</p>
      </Modal>
    </div>
  );
};

export default ViewWorkflow;
